/* eslint-disable react/prop-types */
import React from "react";
import Container from "./Container";
import { ThreadCard } from "./Infographic";
import { ScrollRevealStagger, ScrollRevealStaggerItem } from "./ScrollReveal";

const AVATAR_URL = (gender, id) =>
  `https://randomuser.me/api/portraits/${gender}/${id}.jpg`;

const useCases = [
  {
    id: "freelancer",
    avatarInitials: "JD",
    avatarUrl: AVATAR_URL("men", 20),
    role: "Freelance developer",
    avatarBg: "bg-emerald-100 text-emerald-800",
    title: "Find freelance gigs",
    thread: {
      quote: "Hiring: Full-stack developer for our MVP, $5k budget",
      subreddit: "r/forhire",
      tag: "Hiring",
    },
    children: (
      <>
        Find <Mark>hiring</Mark> posts where companies need freelancers. Reply
        with your portfolio and win projects.
      </>
    ),
  },
  {
    id: "marketing",
    avatarInitials: "MK",
    avatarUrl: AVATAR_URL("women", 25),
    role: "Marketing lead",
    avatarBg: "bg-blue-100 text-blue-800",
    title: "Market research",
    thread: {
      quote: "Looking for branding help—logo + brand guidelines for our startup",
      subreddit: "r/marketing",
      source: "LinkedIn",
      tag: "Seeking",
    },
    children: (
      <>
        Spot posts <Mark>seeking</Mark> branding, design, or agency help. Reach
        out when your offer fits.
      </>
    ),
  },
  {
    id: "support",
    avatarInitials: "AS",
    avatarUrl: AVATAR_URL("men", 30),
    role: "Customer success",
    avatarBg: "bg-slate-100 text-slate-800",
    title: "Support & community",
    thread: {
      quote: "Anyone recommend a good onboarding tool? We're evaluating options",
      subreddit: "r/startups",
      tag: "Recommendation",
    },
    children: (
      <>
        Find people <Mark>asking for recommendations</Mark>. Share your product
        when it fits and turn them into customers.
      </>
    ),
  },
];

function Mark(props) {
  return (
    <mark className="rounded bg-emerald-100 px-1 py-0.5 font-medium text-emerald-800">
      {props.children}
    </mark>
  );
}

function Avatar({ initials, avatarUrl, className = "" }) {
  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt=""
        className={`h-11 w-11 shrink-0 rounded-full object-cover border border-slate-200 ${className}`}
      />
    );
  }
  return (
    <div
      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${className}`}
      aria-hidden
    >
      {initials}
    </div>
  );
}

const Testimonials = () => {
  return (
    <Container className="py-10 lg:py-14">
      <ScrollRevealStagger className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
        {useCases.map((useCase) => (
          <ScrollRevealStaggerItem key={useCase.id}>
            <UseCaseCard useCase={useCase} />
          </ScrollRevealStaggerItem>
        ))}
      </ScrollRevealStagger>
    </Container>
  );
};

function UseCaseCard({ useCase }) {
  const { avatarInitials, avatarUrl, role, avatarBg, title, thread, children } = useCase;
  return (
    <article className="flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition hover:border-slate-300 hover:shadow-md">
      {/* Persona header */}
      <div className="flex items-center gap-4 border-b border-slate-100 bg-slate-50/50 px-5 py-4">
        <Avatar initials={avatarInitials} avatarUrl={avatarUrl} className={avatarBg} />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-900">{role}</p>
          <p className="text-xs text-slate-500">Uses LeadZLab to {title.toLowerCase()}</p>
        </div>
      </div>

      {/* Sample lead + benefit */}
      <div className="flex flex-1 flex-col p-5">
        <p className="mb-3 text-xs font-medium uppercase tracking-wide text-slate-400">
          Finds leads like:
        </p>
        <ThreadCard
          quote={thread.quote}
          subreddit={thread.subreddit}
          source={thread.source}
          time=""
          tag={thread.tag}
        />
        <p className="mt-4 flex-1 text-sm leading-relaxed text-slate-600">
          {children}
        </p>
      </div>
    </article>
  );
}

export default Testimonials;
